import { lazy, Suspense, useEffect, useMemo, useRef } from "react";
import { BrowserRouter as Router, Route } from "react-router-dom";
import { matchPath, Redirect, Switch, useLocation } from "react-router";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter5Adapter } from "use-query-params/adapters/react-router-5";
import Helmet from "react-helmet";
import { usePage } from "../hooks/CmsHooks";
import { AnimatePresence } from "motion/react";
import TopNavigation from "../layout/TopNavigation";
import UIMessages from "../components/UIMessages";
import MessageBanner from "../components/MessageBanner";
import {
  accountPageUrl,
  adminPagesUrl,
  articleRedirectBaseUrl,
  b2bCheckoutUrl,
  checkoutUrl,
  claimsTypeFormPageUrlPattern,
  claimsTypePageUrlPattern,
  claimsWholesaleFormUrl,
  faqBaseUrl,
  guidesBaseUrl,
  loginUrl,
  mapBaseUrl,
  orderBaseUrl,
  postPurchaseUrl,
  productTypeBaseUrl,
  resetPasswordUrl,
  shopUrl,
  storiesBaseUrl
} from "../config/urls";
import LoadingAnim from "./LoadingAnim";
import { OverlayCover } from "./modal/GenericOverlay";
import { ToolTipPortal } from "./ToolTip";
import {
  usePathDefaultLanguage,
  usePathLanguages,
  useSetting
} from "../hooks/SettingsHooks";
import { useSiteFeatures } from "../hooks/useSiteFeatures";
import { useCurrentUser, useUser } from "../functional-components/UserContext";
import PrerenderRedirectStatus from "./PrerenderRedirectStatus";
import { useI18n } from "../hooks/TranslationHooks";
import { OverlayContextProvider } from "../functional-components/OverlayContext";
import { NavigationContextProvider } from "../functional-components/NavigationContext";
import UIMessagesTestPage from "./UIMessages/UIMessagesTestPage";
import ProductSupportPage from "../pages/ProductSupportPage";
import { getCurrentSite, htmlLangFor } from "../utils/SiteUtils";
import { sites } from "../config/sites";
import PageNotFoundFragment from "../fragments/PageNotFoundFragment";
import Page from "../layout/Page";
import { CountryRedirectHandler } from "./CountryRedirection/CountryRedirectHandler";
import { CountryRedirectContextProvider } from "./CountryRedirection/CountryRedirectContext";

const ResetPasswordPage = lazy(() => import("../pages/ResetPasswordPage"));
const LoginPage = lazy(() => import("../pages/LoginPage"));
const AccountPages = lazy(() => import("../pages/AccountPages"));
const ShopFrontPage = lazy(() => import("../pages/ShopFrontPage"));
const StoriesPage = lazy(() => import("../pages/StoriesPage"));
const StoryPage = lazy(() => import("../pages/StoryPage"));
const GuidePage = lazy(() => import("../pages/GuidePage"));
const GuideCategoryPage = lazy(() => import("../pages/GuideCategoryPage"));
const ProductGuidesPage = lazy(() => import("../pages/ProductGuidesPage"));
const GuidesPage = lazy(() => import("../pages/GuidesPage"));
const NewsListPage = lazy(() => import("../pages/NewsListPage"));
const NewsArticlePage = lazy(() => import("../pages/NewsArticlePage"));
const FaqPage = lazy(() => import("../pages/FaqPage"));
const FaqItemPage = lazy(() => import("../pages/FaqPage/FaqItemPage"));
const ClaimTypePage = lazy(() => import("../pages/ClaimTypePage"));
const ClaimTypeFormPage = lazy(() => import("../pages/ClaimTypePage/b2c_form"));
const ClaimWholesaleFormPage = lazy(() =>
  import("../pages/ClaimTypePage/b2b_form")
);
const MapPage = lazy(() => import("../pages/MapPage"));
const SearchPage = lazy(() => import("../pages/SearchPage"));
const ProductsPage = lazy(() => import("../pages/ProductsPage"));
const ProductPage = lazy(() => import("../pages/ProductPage"));
const ProductPrintPage = lazy(() => import("../pages/ProductPage/print"));
const ProductTypePrintPage = lazy(() =>
  import("../pages/ProductTypePage/print")
);
const ProductLabelPrintPage = lazy(() =>
  import("../pages/ProductLabelPrintPage")
);
const ProductPriceTagPrintPage = lazy(() =>
  import("../pages/ProductPriceTagPrintPage")
);
const B2BCheckoutPage = lazy(() => import("../pages/CheckoutPage/b2b"));
const CheckoutSuccessResurs = lazy(() =>
  import("../pages/CheckoutSuccessResurs")
);
const CheckoutFailResurs = lazy(() => import("../pages/CheckoutFailResurs"));
const PostPurchasePage = lazy(() => import("../pages/PostPurchasePage"));
const CmsPage = lazy(() => import("../pages/CmsPage"));
const OrderDetailsPage = lazy(() => import("../pages/OrderDetailsPage"));
const CmsPageLivePreview = lazy(() => import("../pages/CmsPageLivePreview"));
const AdminPages = lazy(() => import("../pages/AdminPages"));
const ProductPageLivePreview = lazy(() =>
  import("../pages/ProductPageLivePreview")
);
const StoryPageLivePreview = lazy(() =>
  import("../pages/StoryPageLivePreview")
);
const LegacyRedirects = lazy(() => import("./LegacyRedirects"));
const CartRoutes = lazy(() => import("./Cart/CartRoutes"));
const ProductSelectTestPage = lazy(() => import("./ProductSelect/testpage"));
const ClaimFormFieldTestPage = lazy(() =>
  import("../pages/ClaimTypePage/ClaimFormFieldTestPage")
);
const DownloadPage = lazy(() => import("../pages/DownloadPage"));
const CheckoutPageRoute = lazy(() => import("./routes/CheckoutPageRoute"));

const CmsPageRoute = props => {
  const path = props.slug || "/" + props.match.params.path;
  return (
    <CmsPage
      key={"cms-page-" + path} /* This forces a mount of a new page */
      {...props}
      path={path}
    />
  );
};

const CmsPageOverlay = props => {
  return (
    <CmsPageRoute
      key={props.match.path}
      displayAsOverlay
      {...props}
      slug={props.location.pathname}
    />
  );
};

const StoriesPageRoute = props => {
  const page = usePage(storiesBaseUrl);
  return <StoriesPage page={page} {...props} />;
};

const StoryPageRoute = props => {
  return <StoryPage {...props} />;
};

const StoryPageOverlay = props => {
  return <StoryPageRoute key={props.match.path} displayAsOverlay {...props} />;
};

const NewsArticlePageRoute = props => {
  return <NewsArticlePage {...props} />;
};

const ClaimTypePageRoute = props => {
  return <ClaimTypePage {...props} />;
};

const ClaimTypeFormPageRoute = props => {
  return <ClaimTypeFormPage {...props} />;
};

const ClaimWholesaleFormPageRoute = props => {
  return <ClaimWholesaleFormPage {...props} />;
};

const ProductsPageRoute = props => {
  return <ProductsPage {...props} />;
};

const ProductPageRoute = props => {
  const artId = props.match.params.art_id;
  return <ProductPage key={artId} {...props} />;
};
const ProductOverlay = props => {
  return <ProductPageRoute key={props.match.url} displayAsOverlay {...props} />;
};
const FaqItemPageRoute = props => {
  return <FaqItemPage {...props} />;
};
const FaqItemOverlay = props => (
  <FaqItemPageRoute displayAsOverlay {...props} />
);

const ProductLabelPrintPageRoute = props => {
  return <ProductLabelPrintPage {...props} />;
};
const ProductPriceTagPrintPageRoute = props => {
  return <ProductPriceTagPrintPage {...props} />;
};

const overlayRoutes = [
  {
    path: "/övriga-golvtyper",
    component: CmsPageOverlay
  },
  {
    path: "/vad-kostar-ett-trägolv",
    component: CmsPageOverlay
  },
  {
    path: "/underhåll",
    component: CmsPageOverlay
  },
  {
    path: "/shop/:category/:art_id-*",
    component: ProductOverlay
  },
  {
    path: storiesBaseUrl + "/:story_slug",
    component: StoryPageOverlay
  },
  {
    path: articleRedirectBaseUrl + "/:art_id",
    component: ProductOverlay
  },
  {
    path: faqBaseUrl + "/:category/q/:question",
    component: FaqItemOverlay
  }
];

//Returns an overlay route matching location, if any
export const matchOverlayRoute = location => {
  if (location && location.pathname) {
    return overlayRoutes.find(
      route => matchPath(location.pathname, route) !== null
    );
  }
};

const LocalizedRouter = () => {
  const location = useLocation();
  const prevLocation = useRef(false);
  const { b2b } = useUser();
  let mainLocation = location;
  console.log("location:", location.pathname);
  const redirects = useSetting("redirects", []);
  const features = useSiteFeatures();

  const routes = [
    { path: "/test/product-select", component: ProductSelectTestPage },
    { path: "/test/ui-messages", component: UIMessagesTestPage },
    { path: "/test/claim-form-field", component: ClaimFormFieldTestPage },
    { path: "/shop/product.php", component: LegacyRedirects },
    { path: "/p/*/:art_id/", component: LegacyRedirects },
    { path: "/shop/list_products.php", component: LegacyRedirects },
    { path: "/shop/story-*", component: LegacyRedirects },
    { path: "/api/pdf/:pdf_filename", component: DownloadPage },
    { exact: true, path: resetPasswordUrl, component: ResetPasswordPage },
    { exact: true, path: loginUrl, component: LoginPage },
    { path: accountPageUrl, component: AccountPages },
    { path: adminPagesUrl, component: AdminPages },
    ...(b2b
      ? [
          {
            exact: true,
            path: b2bCheckoutUrl,
            component: B2BCheckoutPage
          }
        ]
      : [
          {
            exact: true,
            path: checkoutUrl,
            component: CheckoutPageRoute
          }
        ]),
    {
      exact: true,
      path: postPurchaseUrl,
      component: PostPurchasePage
    },
    {
      path: "/shop/checkout/success/resurs/:orderUuid",
      component: CheckoutSuccessResurs
    },
    {
      path: "/shop/checkout/fail/resurs/:orderUuid",
      component: CheckoutFailResurs
    },
    {
      exact: true,
      path: shopUrl,
      component: ShopFrontPage
    },
    {
      path: articleRedirectBaseUrl + "/:art_id",
      component: ProductPageRoute
    },
    {
      path: "/internal/print-label",
      component: ProductLabelPrintPageRoute
    },
    {
      path: "/internal/price-tag",
      component: ProductPriceTagPrintPageRoute
    },
    {
      path: "/shop/:category/:art_id-*",
      component: ProductPageRoute
    },
    {
      path: "/shop/:category",
      component: ProductsPageRoute
    },
    { exact: true, path: "/pageLivePreview", component: CmsPageLivePreview },
    {
      exact: true,
      path: "/productLivePreview",
      component: ProductPageLivePreview
    },
    {
      exact: true,
      path: "/storyPageLivePreview",
      component: StoryPageLivePreview
    },
    { exact: true, path: storiesBaseUrl, component: StoriesPageRoute },
    { path: storiesBaseUrl + "/type/:type", component: StoriesPageRoute },
    { path: storiesBaseUrl + "/:story_slug", component: StoryPageRoute },
    {
      path: productTypeBaseUrl + "/:product_type_path?/:art_slug?",
      component: ProductSupportPage
    },
    /*{
      path: productTypeBaseUrl + "/:path",
      component: ProductTypePage
    },*/
    {
      path: productTypeBaseUrl,
      component: ProductSupportPage,
      exact: true
    },
    ...(features.guides()
      ? [
          { exact: true, path: guidesBaseUrl, component: GuidesPage },
          {
            exact: true,
            path: guidesBaseUrl + "/p/:art_id([0-9]*)",
            component: ProductGuidesPage
          },
          {
            exact: true,
            path: guidesBaseUrl + "/c/:category_slug",
            component: GuideCategoryPage
          },
          {
            path: guidesBaseUrl + "/p/:art_id([0-9]*)/:guide_slug/:step?",
            component: GuidePage
          },
          { path: guidesBaseUrl + "/:guide_slug/:step?", component: GuidePage }
        ]
      : []),

    {
      path: faqBaseUrl + "/:category/q/:question",
      component: FaqItemPageRoute
    },
    {
      path: faqBaseUrl + "/:category?/:tag?",
      component: FaqPage
    },

    { exact: true, path: mapBaseUrl, component: MapPage },

    { exact: true, path: "/news", component: NewsListPage },
    { path: "/news/:news_id-*", component: NewsArticlePageRoute },
    {
      path: claimsTypeFormPageUrlPattern,
      component: ClaimTypeFormPageRoute
    },
    {
      exact: true,
      path: claimsTypePageUrlPattern,
      component: ClaimTypePageRoute
    },
    { path: claimsWholesaleFormUrl(), component: ClaimWholesaleFormPageRoute },
    {
      path: "/search",
      component: SearchPage
    },
    {
      path: orderBaseUrl + "/:uuid",
      component: OrderDetailsPage
    },
    { path: "/cart", component: CartRoutes },
    {
      path: "/dummy-error",
      component: () => {
        throw new Error("Dummy error");
      }
    },
    {
      path: "/docs/:path(.*)",
      component: DownloadPage
    },
    {
      path: "/:path(.*)",
      component: CmsPageRoute
    }
  ];

  if (!features.login()) {
    redirects.push({ from: loginUrl, to: "/", exact: true });
    redirects.push({ from: accountPageUrl, to: "/" });
  }
  if (!features.shop()) {
    //This might seem confusing, but /shop/floors is acting like a catalog in this case
    //redirects.push({ from: "/shop", to: "/shop/floors", exact: true });
    redirects.push({ from: checkoutUrl, to: "/" });
  }
  if (!features.products()) {
    redirects.push({ from: "/shop", to: "/" });
  }

  const firstOverlayMatch = matchOverlayRoute(location);
  const isOverlay = firstOverlayMatch !== undefined;
  //The route will be displayed as overlay - if there is a previous page to display it over
  const noOverlay = location?.state?.noOverlay === true;
  const displayAsOverlay =
    isOverlay &&
    prevLocation.current &&
    mainLocation.pathname !== prevLocation.current.pathname &&
    !noOverlay;
  if (displayAsOverlay) {
    mainLocation = prevLocation.current;
    //console.log("display as overlay");
  } else {
    //Store it for use as overlay background later
    prevLocation.current = location;
    //console.log("set prevLocation:", location.pathname);
  }

  //console.log("prevLocation:", prevLocation.current.pathname);
  //console.log("mainLocation:", mainLocation.pathname);

  const isPrint = location.pathname.includes("/print/");

  useEffect(() => {
    if (window.bjelinLoadingAnim) window.bjelinLoadingAnim.hide();
  }, []);

  return (
    <>
      <AnimatePresence initial={false}>
        <Suspense
          fallback={isPrint ? null : <LoadingAnim log={"LocalizedRouter"} />}
        >
          <Switch location={mainLocation}>
            {redirects.flatMap(({ from, to, ...rest }) =>
              (Array.isArray(from) ? from : [from]).map(from => (
                <Redirect key={from} from={from} to={to} {...rest} />
              ))
            )}
            {routes.map(({ path, component, ...rest }) => {
              return (
                <Route key={path} path={path} component={component} {...rest} />
              );
            })}
          </Switch>
        </Suspense>
      </AnimatePresence>
      <AnimatePresence>
        <Switch>
          {displayAsOverlay && (
            <OverlayCover>
              <Suspense
                fallback={isPrint ? null : <LoadingAnim log={"overlay"} />}
              >
                {overlayRoutes.map(({ path, component, ...rest }) => {
                  return (
                    <Route
                      key={path}
                      path={path}
                      component={component}
                      {...rest}
                    />
                  );
                })}
              </Suspense>
            </OverlayCover>
          )}
        </Switch>
      </AnimatePresence>
    </>
  );
};

function InitUserSecrets() {
  useCurrentUser(); //prepares the user context and sets the user secrets in userService
  return null;
}

const LocalizedRoute = ({ match }) => {
  const site = getCurrentSite();
  const i18n = useI18n();
  const pathLang = match.params.lang;
  const lang = getCurrentSite() === "us" ? "en-us" : pathLang;
  const htmlLang = useMemo(() => htmlLangFor(site, pathLang), [site, pathLang]);

  const path = match.params.path;
  const isInternal = path.includes("/internal/");
  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [i18n, lang]);
  console.log("LocalizedRoute lang", lang);
  return (
    <Router basename={"/" + pathLang}>
      <QueryParamProvider adapter={ReactRouter5Adapter}>
        <Helmet>
          <html lang={htmlLang} />
        </Helmet>
        <Suspense fallback={null}>
          <InitUserSecrets />
        </Suspense>
        <PrerenderRedirectStatus lang={pathLang} />
        <NavigationContextProvider lang={lang} pathLang={pathLang}>
          <CountryRedirectContextProvider>
            <OverlayContextProvider>
              <Suspense fallback={<LoadingAnim log={"router"} />}>
                <UIMessages />
                <MessageBanner>
                  <CountryRedirectHandler />
                </MessageBanner>
                {!isInternal && <TopNavigation />}
                <ToolTipPortal />
                <LocalizedRouter />
              </Suspense>
            </OverlayContextProvider>
          </CountryRedirectContextProvider>
        </NavigationContextProvider>
      </QueryParamProvider>
    </Router>
  );
};

const PrintRoute = ({ match }) => {
  const i18n = useI18n();
  const site = match.params.site;
  const pathLang = match.params.lang;
  const lang = site === "us" ? "en-us" : pathLang;

  useEffect(() => {
    if (window.bjelinLoadingAnim) window.bjelinLoadingAnim.hide();
  }, []);

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [i18n, lang]);

  const ProductPrintPageRoute = props => {
    return <ProductPrintPage {...props} />;
  };
  const ProductTypePrintPageRoute = props => {
    return <ProductTypePrintPage {...props} />;
  };

  const MissingPageRoute = () => {
    return (
      <Page title={""} name={"404"} transition="simple">
        <PageNotFoundFragment />
      </Page>
    );
  };

  const routes = [
    {
      path: "/product/:art_id",
      component: ProductPrintPageRoute
    },
    {
      path: "/product-type/:product_type_path",
      component: ProductTypePrintPageRoute
    }
  ];

  return (
    <Router basename={"/print/" + site + "/" + pathLang}>
      <InitUserSecrets />
      <NavigationContextProvider lang={lang} pathLang={pathLang}>
        <Suspense fallback={null}>
          <Switch>
            {routes.map(({ path, component, ...rest }) => {
              return (
                <Route key={path} path={path} component={component} {...rest} />
              );
            })}
            <Route path="/**" component={MissingPageRoute} />
          </Switch>
        </Suspense>
      </NavigationContextProvider>
    </Router>
  );
};

//Add language as prefix, when language param is missing
const MissingLangRedirect = ({ location }) => {
  const default_language = usePathDefaultLanguage();
  const languages = usePathLanguages();
  const path = location.pathname ? location.pathname : "/";
  const userLang = navigator.language ? navigator.language.substr(0, 2) : false;
  const redirectLang = languages.includes(userLang)
    ? userLang
    : default_language;
  return <Redirect to={"/" + redirectLang + path + (location.search || "")} />;
};

const UnsupportedLangRedirect = ({ location }) => {
  const default_language = usePathDefaultLanguage();
  const languages = usePathLanguages();
  const path = location.pathname.substr(3) || "/";
  console.log("UnsupportedLangRedirect", path);
  const userLang = navigator.language ? navigator.language.substr(0, 2) : false;
  const redirectLang = languages.includes(userLang)
    ? userLang
    : default_language;
  return <Redirect to={"/" + redirectLang + path + (location.search || "")} />;
};

const Routes = () => {
  const languages = usePathLanguages();
  const langParam = "/:lang(" + languages.join("|") + ")";
  const siteParam = "/:site(" + sites.join("|") + ")";

  return (
    <Switch>
      <Route path={langParam + ":path(.*)"} component={LocalizedRoute} />
      <Route
        path="/:unsupportedLang([a-z]{2})/"
        component={UnsupportedLangRedirect}
      />
      <Route
        path={"/print" + siteParam + langParam + ":path(.*)"}
        component={PrintRoute}
      />
      <Route path="/**" component={MissingLangRedirect} />
    </Switch>
  );
};

export default Routes;
